/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { RoutesInterface } from '../../types/routes';
import { UserManagementRoute } from '../../apps/company-tracker/routes/UserManagementRoute';
import Summary from '../summary';
import ManagementFramework from '../management-framework';
import CustomMetrics from '../custom-metrics';
import SystemLog from '../../components/system-log';
import { Switch, Route } from 'react-router-dom';
import Loader from '../../components/loader';
import { useAppSelector } from '../../reducers';
import { rootAppPath, getRootAppPath } from './utils';
import DownloadsRoute from './DownloadsRoute';
import { adminRoute } from '../admin-dashboard/AdminDashboardRoute';
import { DataShareRoute } from '../data-share/DataShareRoute';
import { ReferralsRoute } from '../referrals/ReferralsRoute';
import AdminSettings from '../../components/admin-settings';
import { SurveyTemplatesRoute } from '../survey-template';
import { BulkImporting } from '../../components/bulk-importing';
import { CompanySettingsRoute } from './CompanySettingsRoute';
import BankingSettings from '../../components/banking-settings';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { AppRoute } from '../AppRoute';
import { SubscriptionInfoRoute } from '../../apps/company-tracker/routes/subscription-info';
import { SubsidiaryUserDelegation } from '../admin-dashboard/components/SubsidiaryUserDelegation';
import { InitiativePermissions } from '../../services/permissions/InitiativePermissions';
import { InsightRoute } from './InsightRoute';
import { InitiativeStructureContainer } from '../initiative-structure/InitiativeStructureContainer';

const routeDefaults = {
  auth: true,
  getRootAppPath: getRootAppPath,
  requiresInitiativeId: true,
  appPermissionId: 'app_company_tracker',
}

export const companyTrackerInitiativeRoutes: RoutesInterface = {
  NAVIGATE_BY_MAP: {
    ...routeDefaults,
    id: 'navigation_map',
    label: 'Organisational structure',
    path: `/${rootAppPath}/map/:initiativeId`,
    icon: 'fa-project-diagram',
    tooltip: 'Navigation Tree of the Reporting Level Dashboards',
    component: InitiativeStructureContainer,
  },
  NAVIGATE_BY_ARCHIVED: {
    ...routeDefaults,
    id: 'archived_map',
    label: 'Archived initiatives',
    path: `/${rootAppPath}/map/:initiativeId/archived`,
    icon: 'fa-project-diagram',
    tooltip: 'Navigation Tree of the Archived Reporting Level Dashboards',
    component: InitiativeStructureContainer,
  },
  SUMMARY_CURRENT: {
    ...routeDefaults,
    id: 'summary',
    label: 'Insights',
    path: `/${rootAppPath}/summary/:initiativeId?/:page?(current)/:summaryPage?`,
    component: Summary,
    navItemPermissionId: 'menu_summary',
    tooltip: 'Company profile dashboard',
  },
  CUSTOM_DASHBOARD: {
    ...routeDefaults,
    id: 'custom_dashboard',
    label: 'Custom Dashboard',
    // @TODO: remove surveyId since custom dashboard don't use it
    path: `/${rootAppPath}/summary/:initiativeId/:surveyId?/custom-dashboard/:dashboardId`,
    component: InsightRoute,
    navItemPermissionId: 'menu_summary',
    requiredUserRoles: InitiativePermissions.canAccessInsightsAndDownloadsRoles,
  },
  SUMMARY_STAFF_ONLY: {
    ...routeDefaults,
    id: 'summary_staff_only',
    label: 'Insights',
    path: `/${rootAppPath}/summary/:initiativeId/:surveyId?/summary-staff-only/:summaryPage`,
    component: InsightRoute,
    navItemPermissionId: 'menu_summary',
    requiredUserRoles: InitiativePermissions.canAccessInsightsAndDownloadsRoles,
    tooltip: 'Company profile dashboard',
  },
  SUMMARY: {
    ...routeDefaults,
    id: 'summary',
    label: 'Insights',
    path: `/${rootAppPath}/summary/:initiativeId?/:surveyId?/:summaryPage?`,
    component: InsightRoute,
    navItemPermissionId: 'menu_summary',
    requiredUserRoles: InitiativePermissions.canAccessInsightsAndDownloadsRoles,
    tooltip: 'Company profile dashboard',
  },
  DOWNLOADS: {
    ...routeDefaults,
    id: 'downloads',
    label: 'Downloads',
    path: `/${rootAppPath}/downloads/:initiativeId?/:surveyId?`,
    component: DownloadsRoute,
    appPermissionId: 'app_company_tracker',
    tooltip: 'Downloads',
    requiresInitiativeId: true,
    requiredUserRoles: InitiativePermissions.canAccessInsightsAndDownloadsRoles,
    canAccessFeaturePermission: FeaturePermissions.canAccessDownloads
  },
  DOWNLOADS_CUSTOM: {
    ...routeDefaults,
    id: 'custom_downloads',
    label: 'Custom Downloads',
    path: `/${rootAppPath}/downloads/:initiativeId?/custom/:action(create|manage)`,
    defaultParams: {
      action: 'manage'
    },
    component: DownloadsRoute,
    appPermissionId: 'app_company_tracker',
    tooltip: 'Custom Downloads',
    requiresInitiativeId: true,
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessDownloads
  },
  MANAGE_USERS: {
    ...routeDefaults,
    id: 'manage_users',
    label: 'Manage Users',
    path: `/${rootAppPath}/admin/:initiativeId/manage-users/:page?`,
    icon: 'fa-user',
    tooltip: 'Manage Users and Permissions',
    component: UserManagementRoute,
    requiredUserRoles: InitiativePermissions.canManageRoles,
  },
  MANAGEMENT_FRAMEWORK: {
    ...routeDefaults,
    id: 'management_framework',
    label: 'Management framework',
    path: `/${rootAppPath}/management-framework/:initiativeId?`,
    component: ManagementFramework,
    navItemPermissionId: 'menu_frameworks',
    hidden: true,
  },
  CUSTOM_METRICS:
  {
    ...routeDefaults,
    id: 'custom_metrics',
    label: 'Custom Metrics',
    path: `/${rootAppPath}/admin/:initiativeId?/custom-metrics/:groupId?/:view?`,
    component: CustomMetrics,
    tooltip: 'Custom Metrics',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
  },
  DATA_SHARE_INITIATIVE: {
    ...routeDefaults,
    id: 'data_share_initiative',
    label: 'Data Share',
    path: `/${rootAppPath}/admin/:initiativeId/data-share/:shareId?`,
    component: DataShareRoute,
    tooltip: 'Data Share',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
  },
  REFERRALS: {
    ...routeDefaults,
    id: 'referrals',
    label: 'Referrals',
    path: `/${rootAppPath}/admin/:initiativeId/referrals`,
    component: ReferralsRoute,
    tooltip: 'Referrals',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
  },
  ADMIN_DASHBOARD: { ...routeDefaults, ...adminRoute },
  SYSTEM_LOG:
  {
    ...routeDefaults,
    id: 'system_log',
    label: 'System Log',
    path: `/${rootAppPath}/admin/:initiativeId/system-log`,
    component: SystemLog,
    tooltip: 'System Log',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessAdminDashboard
  },
  SUBSIDIARY_USER_DELEGATION:
  {
    ...routeDefaults,
    id: 'subsidiary_user_delegation',
    label: 'User Delegation',
    path: `/${rootAppPath}/admin/:initiativeId/user/:userId/delegation`,
    component: SubsidiaryUserDelegation,
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessBulkDelegation
  },
  COMPANY_SETTINGS:
  {
    ...routeDefaults,
    id: 'company_settings',
    label: 'Company Settings',
    path: `/${rootAppPath}/admin/:initiativeId/company-settings/:page?`,
    component: CompanySettingsRoute,
    tooltip: 'Company Settings',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    defaultParams: { page: 'details' },
  },
  BANKING_SETTINGS:
  {
    ...routeDefaults,
    id: 'banking_settings',
    label: 'Banking Settings',
    path: `/${rootAppPath}/admin/:initiativeId/banking-settings`,
    component: BankingSettings,
    tooltip: 'Banking Settings',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
  },
  ADMIN_SETTINGS:
  {
    ...routeDefaults,
    id: 'admin',
    label: 'Admin',
    path: `/${rootAppPath}/admin/:initiativeId`,
    component: AdminSettings,
    tooltip: 'Admin',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true
  },
  BULK_IMPORTING: {
    ...routeDefaults,
    id: 'bulk_reporting',
    label: 'Bulk Reporting',
    path: `/${rootAppPath}/bulk-importing/:initiativeId`,
    component: BulkImporting,
    tooltip: 'Bulk Reporting',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessBulkImporting
  },
  SUBSCRIPTION_INFO: {
    ...routeDefaults,
    id: 'subscription_info',
    label: 'Subscription info',
    path: `/${rootAppPath}/subscription-info/:initiativeId`,
    component: SubscriptionInfoRoute,
    tooltip: 'Subscription info',
  },
  SURVEY_TEMPLATES:
  {
    ...routeDefaults,
    id: 'survey_templates',
    label: 'Survey Templates',
    path: `/${rootAppPath}/survey-templates/:initiativeId`,
    component: SurveyTemplatesRoute,
    tooltip: 'Survey Templates',
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessSurveyTemplates
  },
  SURVEY_TEMPLATES_VIEW:
  {
    ...routeDefaults,
    id: 'survey_templates_view',
    label: 'Survey Templates',
    path: `/${rootAppPath}/survey-templates/:initiativeId/template/:templateId/:page`,
    component: SurveyTemplatesRoute,
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessSurveyTemplates
  },
  SURVEY_TEMPLATES_HISTORY:
  {
    ...routeDefaults,
    id: 'survey_templates_history',
    label: 'Survey Templates History',
    path: `/${rootAppPath}/survey-templates/:initiativeId/template/:templateId/history/:historyId/:page?`,
    component: SurveyTemplatesRoute,
    requiredUserRoles: InitiativePermissions.canManageRoles,
    hideOnMissingPermission: true,
    canAccessFeaturePermission: FeaturePermissions.canAccessSurveyTemplates
  },
}

export const CompanyTrackerInitiativeRoute = () => {

  const initiativeState = useAppSelector((state) => state.initiative);
  if (!initiativeState.loaded) {
    return <Loader />;
  }

  // All of these must load initiatives
  return (
    <Switch>
      {Object.entries(companyTrackerInitiativeRoutes).map(([, route]) => (
        <Route
          key={`route_${route.id}`}
          path={route.path}
          exact={route.exact}
          render={() => <AppRoute route={route} />}
        />
      ))}
    </Switch>
  )
}
